import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { toast, ToastContainer } from 'react-toastify';

import PropTypes from 'prop-types';
import { Modal, Table } from 'semantic-ui-react';

import CustomDropdown from '../../../../RecruiterVerificationDashboard/components/CustomDropdown';
import Button from '../../../../../components/Button';

import Loader from '../../../../../components/Loader';
import getUnverifiedCompanies from '../../../../../services/getUnverifiedCompanies';
import getUnverifiedRecruiters from '../../../../../services/getUnverifiedRecruiters';
import getUnverifiedRecruitersCount from '../../../../../services/getUnverifiedRecruitersCount';

import './styles.scss';

const VerifyModal = props => {
    const { adminId, recruiterData = {}, isVerifyLoading = false, onVerifyCompany, onClose } = props;

    const [recData, setRecData] = useState({
        list: [],
        isLoading: false,
        pageNumber: 1,
        pageContent: 50,
        isFetchedAll: false,
        fetch: false,
        total: 0,
    });

    const [getRecs, setGetRecs] = useState(false);

    const [companyData, setCompanyData] = useState({
        isLoading: true,
        data: null,
    });

    const [companySelectData, setCompanySelectData] = useState({
        list: [],
        isLoading: false,
        searchText: '',
        noResultFound: false,
    });

    // when user clicks on verify company, confirm with pop up message
    const handleVerifyCompany = () => {
        const userComfirmation = confirm(`Verifying this company will verify all ${recData.total} recruiters associated with it. Do you want to continue?`);
        
        if (userComfirmation) {
            onVerifyCompany(recruiterData.unverifiedCompanyId, companyData.data.companyData);
        }
    }

    // when get recruiters button is clicked
    const handleGetRecruiters = () => {
        if (!companyData || !companyData.data || !companyData.data.domains) {
            return;
        }

        setGetRecs(true);
        
        setRecData({
            fetch: true,
            pageNumber: 1,
            pageContent: 50,
            list: [],
            total: 0,
            isLoading: false,
            isFetchedAll: false,
        });
    };

    const handleClearList = () => {
        setCompanySelectData({
            list: [],
            isLoading: false,
            searchText: '',
        });
    };

    const handleCompanySelect = (id, label) => {
        setCompanyData({
            isLoading: true,
            unverifiedCompanyId: id,
            unverifiedCompanyName: label,
        })
    }

    const handleInputChange = text => {
        setCompanySelectData(prevObj => {
            return {
                ...prevObj,
                searchText: text
            }
        });
    };

    const handleLoadMoreRecruiters = () => {
        getRecruiters();
    }

    const getRecruiterCount = async () => {
        if (!companyData || !companyData.data || !companyData.data.domains) {
            return;
        }

        try {
            const response = await getUnverifiedRecruitersCount(adminId, {
                unverifiedCompanyId: companyData.unverifiedCompanyId,
            });

            setRecData(prevObj => {
                return {
                    ...prevObj,
                    total: response.data.data[0].count,
                }
            })
        } catch (error) {
            // console.log(error)
        }
    }

    const getRecruiters = async () => {
        if (!companyData || !companyData.data || !companyData.data.domains) {
            return;
        }

        setRecData(prevObj => {
            return {
                ...prevObj,
                isLoading: true,
            }
        });

        try {
            const response = await getUnverifiedRecruiters(adminId, {
                unverifiedCompanyId: companyData.unverifiedCompanyId,
                pageNumber: recData.pageNumber || 1,
                pageContent: recData.pageContent || 50,
                returnList: 1,
            });

            if (response && response.data && response.data.data) {
                if (response.data.data.length > 0) {
                    setRecData(prevObj => {
                        return {
                            ...prevObj,
                            pageNumber: prevObj.pageNumber ? prevObj.pageNumber + 1 : 2,
                            list: prevObj.list ? prevObj.list.concat(response.data.data) : response.data.data,
                            isLoading: false,
                            isFetchedAll: response.data.data.length < prevObj.pageContent ? true : false,
                            fetch: true,
                        }
                    })
                } else {
                    setRecData(prevObj => {
                        return {
                            ...prevObj,
                            isLoading: false,
                            isFetchedAll: true,
                            fetch: true,
                        }
                    })
                }
            }
        } catch (error) {
            setRecData(prevObj => {
                return {
                    ...prevObj,
                    isFetchedAll: true,
                    fetch: false,
                }
            })
        }
    }

    const getCompanies = async () => {

        if (!companySelectData.searchText) {
            return;
        }

        setCompanySelectData({ isLoading: true, list: [] });
        
        try {
            const response = await getUnverifiedCompanies(adminId, {
                displayNameLike: companySelectData.searchText,
                nameSort: 1,
                pageNumber: 1,
                pageContent: 50,
                status: 1,
                withProductInfo: 1,
                targetDomain: process.env.REACT_APP_PLATFORM,
            });

            let noResultFound = false;

            if (response && response.status == 200) {
                if (response.data && response.data.data && response.data.data.data && response.data.data.data.length > 0) {
                    setCompanySelectData({
                        isLoading: false,
                        noResultFound: false,
                        list: response.data.data.data,
                    });
                } else {
                    noResultFound = true;
                }
            } else {
                noResultFound = true;
            }

            if (noResultFound) {
                setCompanySelectData({ list: [], isLoading: false, noResultFound: true });
            }
        } catch (error) {
            setCompanySelectData({ isLoading: false, list: [] });   
        }
    }

    // fetch companies list after every 300 ms of input being changed
    useEffect(() => {
        let timerId = null;

        try {
            timerId = setTimeout(() => {
                if (!companySelectData.isLoading && companySelectData.searchText && companySelectData.searchText.length > 0) {
                    getCompanies();
                }
            }, 300);
        } catch (error) {
            // console.log(error);
        }

        return () => {
            // clear timeout before unmounting
            if (timerId) {
                clearTimeout(timerId);
            }
        }
    }, [companySelectData.searchText])

    // fetch recruiters list whenever fetch value is true
    useEffect(() => {
        if (recData.fetch) {
            getRecruiters()
            getRecruiterCount()
        }
    }, [recData.fetch]);

    const getUnverifiedCompany = async (unverifiedCompanyId) => {
        try {
            setGetRecs(false);
            
            setRecData({
                fetch: false,
                pageNumber: 1,
                pageContent: 50,
                list: [],
                total: 0,
                isLoading: false,
                isFetchedAll: false,
            });

            const response = await getUnverifiedCompanies(adminId, {
                id: unverifiedCompanyId,
            });

            if (response && response.data && response.data.data && response.data.data.companies && response.data.data.companies.length > 0) {
                const company = response.data.data.companies[0];
                setCompanyData(prevObj => {
                    return {
                        ...prevObj,
                        isLoading: false,
                        data: {
                            id: company.id,
                            displayName: company.displayName,
                            domains: company.domains,
                            companyData: company,
                        },
                        unverifiedCompanyId: company.id,
                        unverifiedCompanyName: company.displayName,
                    }
                });
            } else {
                toast.error('Could not find company associated with the recruiter. Please contact tech team or try again later')
                onClose();
            }
        } catch (error) {
            // console.log(error);
            toast.error('Could not find company associated with the recruiter. Please contact tech team or try again later')
            onClose();
        }
    }

    // select selected company data whenever the unverified company id changes
    useEffect(() => {
        if (companyData && companyData.unverifiedCompanyId) {
            getUnverifiedCompany(companyData.unverifiedCompanyId);
        }
    }, [companyData.unverifiedCompanyId]);

    // fetch selected company data when modal first loads
    useEffect(() => {
        if (adminId && recruiterData.unverifiedCompanyId) {
            setCompanyData({ unverifiedCompanyId: recruiterData.unverifiedCompanyId });
        }
    }, [adminId]);

    return (
        <Modal
            className='verify-company-modal'
            open={true}
            onClose={onClose}
        >
            <Modal.Header>
                Verify Company
            </Modal.Header>

            <Modal.Content className="modal__content">
                <div className='modal__content--select'>
                    <div className='label'>Select Unverified Company</div>

                    <CustomDropdown 
                        className="company-dropdown"
                        placeholder="Select Unverified Company"
                        dropdownList={companySelectData && companySelectData.list ? companySelectData.list.map(item => {
                            return {
                                label: item.displayName,
                                value: item.id,
                            }
                        }) : []}
                        isLoading={companySelectData.isLoading}
                        onInputChange={handleInputChange}
                        onItemSelected={handleCompanySelect}
                        isSelected={companyData && companyData.unverifiedCompanyId ? {
                            displayName: companyData.unverifiedCompanyName,
                            id: companyData.unverifiedCompanyId,
                        } : null}
                        clearList={handleClearList}
                        noResultFound={companySelectData.noResultFound}
                    />
                </div>
                {companyData.isLoading ? (
                    <Loader />
                ) : null}

                {!companyData.isLoading && companyData.data && companyData.data.id ? (
                    <div className='company-details'>
                        <div className='label'>Selected Company</div>
                        <Table>
                            <Table.Header>
                                <Table.HeaderCell>Unverified Company ID</Table.HeaderCell>
                                <Table.HeaderCell>Name</Table.HeaderCell>
                                <Table.HeaderCell>Domains</Table.HeaderCell>
                            </Table.Header>
                            <Table.Body>
                                <Table.Row>
                                    <Table.Cell>{companyData.data.id}</Table.Cell>
                                    <Table.Cell>{companyData.data.displayName}</Table.Cell>
                                    <Table.Cell>{companyData.data.domains}</Table.Cell>
                                </Table.Row>
                            </Table.Body>
                        </Table>
                        <div className='sublabel'>Click on Get Recruiters to check list of recruiters that will be effected on associating the above company to the domain of the recruiter</div>
                    </div>
                ) : null} 

                {getRecs && recData.list ? (
                    <div className='recruiter-list'>
                        <div className='recruiter-list-table'>
                            <Table>
                                <Table.Header>
                                    <Table.HeaderCell>ID</Table.HeaderCell>
                                    <Table.HeaderCell>Name</Table.HeaderCell>
                                    <Table.HeaderCell>Email</Table.HeaderCell>
                                    <Table.HeaderCell>Domains</Table.HeaderCell>
                                    <Table.HeaderCell>Created On</Table.HeaderCell>
                                </Table.Header>
                                
                                {recData.isLoading ? (
                                    <Loader />
                                ) : null}
                                <Table.Body className="table-body">
                                    {recData.list.map(item => {
                                        return (
                                            <Table.Row>
                                                <Table.Cell>{item.id}</Table.Cell>
                                                <Table.Cell>{item.recname || 'NA'}</Table.Cell>
                                                <Table.Cell>{item.email}</Table.Cell>
                                                <Table.Cell>{item.domain}</Table.Cell>
                                                <Table.Cell>{item.date_created ? moment(item.date_created).isValid() ? moment(item.date_created).format('DD-MM-YYYY') : 'NA': 'NA'}</Table.Cell>
                                            </Table.Row>
                                        )
                                    })}
                                </Table.Body>
                            </Table>
                            {!recData.isLoading && recData.list.length === 0 ? (
                                <div>No Recruiters found</div>
                            ) : null}
                        </div>
                        {recData.isLoading ? null : (
                            <div className='recruiter-list-meta'>
                                <div className='count'>Showing {recData.list ? recData.list.length : 0} / {recData.total}</div>
                                {!recData.isFetchedAll ? <div className='btn-load-more' onClick={handleLoadMoreRecruiters}>Load more</div> : null}
                            </div>
                        )}
                    </div>
                ) : null}
            </Modal.Content>

            <Modal.Actions>
                {!companyData.isLoading && !getRecs ? (
                    <Button text="Get Recruiters" skin="dark" clickButton={handleGetRecruiters}/>
                ) : null}

                {getRecs ? (
                    <Button text="Verify" skin="dark" clickButton={handleVerifyCompany} loading={isVerifyLoading} />
                ) : null}
            </Modal.Actions>
        </Modal>
    )
};

VerifyModal.propTypes = {
    adminId: PropTypes.string,
    onClose: PropTypes.func,
    isLoading: PropTypes.bool,
    onVerifyCompany: PropTypes.func,
    isVerifyLoading: PropTypes.bool,
    recruiterData: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        email: PropTypes.string,
        unverifiedCompanyId: PropTypes.string
    }),
};

export default VerifyModal;
